import JSZip from 'jszip';
import { basename, buildImportInfo, findByBasename } from './sd-card-entries';
import { SdCardError, type PackWarning, type SdCardEntry } from './types';
import { finalize, type PackProgress } from './pack-common';

// P2.8 / prisma SMART-SOFT → .psdat.
// Output layout (see P2_8ImportFileProvider, which copies whole day folders):
// config.pscfg + statistic.psstat at root, recordings under
// signals/<yyyymmdd>/..., logs under logs/. Real cards nest each recording in a
// session sub-folder below the day folder, so the sub-path is preserved verbatim.

// Group 2 is the sub-path under the day folder, e.g. "0082/signal_028.wmedf".
const P28_DAY_FILE_RE = /(?:^|\/)(\d{8})\/(.+)$/;
const SIGNAL_EXT_RE = /\.(wmedf|emedf|edf)$/i;
const XML_EXT_RE = /\.xml$/i;
const LOG_SEGMENT_RE = /(?:^|\/)logs?(?:\/|$)/i;

type DayFile = { entry: SdCardEntry; rel: string };

const pushInto = (
  map: Map<string, DayFile[]>,
  key: string,
  value: DayFile,
): void => {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
};

// Last run of digits in a name, e.g. signal_310.wmedf -> 310.
const extractId = (name: string): number => {
  const matches = name.match(/\d+/g);
  return matches ? Number.parseInt(matches[matches.length - 1], 10) : Number.NaN;
};

const byRecordingId = (a: DayFile, b: DayFile): number => {
  const idA = extractId(basename(a.rel));
  const idB = extractId(basename(b.rel));
  if (Number.isNaN(idA) || Number.isNaN(idB)) {
    return a.rel.localeCompare(b.rel);
  }
  return idA - idB;
};

// Forces the .wmedf extension the backend expects, keeping any session sub-folder.
const toSignalPath = (rel: string): string => {
  const slash = rel.lastIndexOf('/');
  const dir = slash >= 0 ? rel.slice(0, slash + 1) : '';
  const name = slash >= 0 ? rel.slice(slash + 1) : rel;
  const dot = name.lastIndexOf('.');
  return dir + (dot > 0 ? name.slice(0, dot) : name) + '.wmedf';
};

export const packP28 = async (
  entries: SdCardEntry[],
  onProgress?: PackProgress,
): Promise<Blob> => {
  const zip = new JSZip();

  const config = findByBasename(entries, 'config.pscfg');
  if (config) zip.file('config.pscfg', config.file);
  const statistic = findByBasename(entries, 'statistic.psstat');
  if (statistic) zip.file('statistic.psstat', statistic.file);

  const signalsByDay = new Map<string, DayFile[]>();
  const eventsByDay = new Map<string, DayFile[]>();
  const logs: SdCardEntry[] = [];

  for (const entry of entries) {
    const dayMatch = entry.path.match(P28_DAY_FILE_RE);
    if (dayMatch) {
      const [, day, rel] = dayMatch;
      const name = basename(rel);
      if (SIGNAL_EXT_RE.test(name)) pushInto(signalsByDay, day, { entry, rel });
      else if (XML_EXT_RE.test(name)) pushInto(eventsByDay, day, { entry, rel });
      continue;
    }
    if (LOG_SEGMENT_RE.test(entry.path)) logs.push(entry);
  }

  const days = Array.from(signalsByDay.keys()).sort();
  if (days.length === 0) {
    throw new SdCardError(
      'No P2.8 therapy recordings (signal_*.wmedf) were found on the card.',
    );
  }

  // Within a day, emit signals then events, each ordered by recording id, so the
  // backend's index-based signal/event pairing (NewP2_8ZipImporter) stays aligned.
  for (const day of days) {
    const signals = (signalsByDay.get(day) ?? []).slice().sort(byRecordingId);
    const events = (eventsByDay.get(day) ?? []).slice().sort(byRecordingId);
    for (const { entry, rel } of signals) {
      zip.file(`signals/${day}/${toSignalPath(rel)}`, entry.file);
    }
    for (const { entry, rel } of events) {
      zip.file(`signals/${day}/${rel}`, entry.file);
    }
  }

  for (const log of logs) {
    zip.file(`logs/${basename(log.path)}`, log.file);
  }

  zip.file('importInfo.txt', buildImportInfo('P2.8'));

  return finalize(zip, onProgress);
};

export const p28ReimportWarning = (entries: SdCardEntry[]): PackWarning | null =>
  findByBasename(entries, 'statistic.psstat')
    ? null
    : {
        title: 'SD card has no statistics file',
        description:
          "This prisma SMART/SOFT card has no statistic.psstat, so the packed .psdat opens in Signal Viewer but prismaTS can't re-import it.",
      };
